import type { Card, GameState, GridPos } from '../types';
import { ROW_WIDTHS } from '../types';
import { canAfford } from './cost';

export function rowWidth(row: number): number {
  if (row < 0 || row >= ROW_WIDTHS.length) return 0;
  return ROW_WIDTHS[row]!;
}

export function isValidPos(pos: GridPos): boolean {
  return pos.col >= 0 && pos.col < rowWidth(pos.row);
}

export function gridIndex(pos: GridPos): number {
  let i = 0;
  for (let r = 0; r < pos.row; r++) i += rowWidth(r);
  return i + pos.col;
}

export function fromIndex(i: number): GridPos {
  let remaining = i;
  for (let r = 0; r < ROW_WIDTHS.length; r++) {
    const w = rowWidth(r);
    if (remaining < w) return { row: r, col: remaining };
    remaining -= w;
  }
  throw new Error(`fromIndex: out of range ${i}`);
}

export function neighbors(pos: GridPos): GridPos[] {
  const { row, col } = pos;
  const candidates: GridPos[] = [
    { row, col: col - 1 },
    { row, col: col + 1 },
    { row: row + 1, col: col - 1 },
    { row: row + 1, col },
  ];
  return candidates.filter(isValidPos);
}

export function samePos(a: GridPos, b: GridPos): boolean {
  return a.row === b.row && a.col === b.col;
}

export function isAdjacentToLastAcquired(
  card: Card,
  lastAcquired: GridPos | null
): boolean {
  if (lastAcquired === null) return card.row === 0;
  return neighbors(lastAcquired).some((p) => samePos(p, { row: card.row, col: card.col }));
}

export function isAcquirable(card: Card, state: GameState): boolean {
  if (card.acquired) return false;
  if (!canAfford(card.cost, card.placedDice)) return false;
  return isAdjacentToLastAcquired(card, state.lastAcquired);
}

export function findCard(
  pyramid: Card[],
  pos: GridPos
): Card | undefined {
  return pyramid.find((c) => c.row === pos.row && c.col === pos.col);
}
